import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UnidadeMedidaDeItemService } from '../../../services/configuracoes/unidade-de-medida.service';

@Component({
  selector: 'app-select-unidade-medida',
  templateUrl: './select-unidade-medida.component.html',
  styleUrls: ['./select-unidade-medida.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectUnidadeMedidaComponent),
      multi: true
    }
  ]
})
export class SelectUnidadeMedidaComponent
  implements ControlValueAccessor, OnInit, OnDestroy {

  /* ======================= *
   * Inputs
   * ======================= */
  @Input() placeholder: string = '';
  @Input() returnType: 'object' | 'id' = 'object';
  @Input() showAddButton: boolean = true;

  /* ======================= *
   * Estado interno
   * ======================= */
  unidades: any[] = [];
  loading = false;

  /** valor que o p-dropdown usa */
  value: any = null;

  /** Visibilidade do dialog de nova unidade */
  dialogVisible: boolean = false;

  /** Texto do botao "+ Agregar Unidade de Medida" (carregado via i18n) */
  textoAgregarUnidade: string = '';

  private langSub?: Subscription;

  /* ======================= *
   * ControlValueAccessor
   * ======================= */
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private unidadeMedidaService: UnidadeMedidaDeItemService,
    private translate: TranslateService
  ) { }

  /* ======================= *
   * Lifecycle
   * ======================= */
  ngOnInit(): void {
    this.loadInitial();
    this.loadTextoAgregar();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTextoAgregar();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /* ======================= *
   * ControlValueAccessor impl
   * ======================= */
  writeValue(value: any): void {
    this.value = value;

    // Se ja recebeu valor (edicao) e a lista ja existe, sincroniza
    if (value && this.unidades.length) {
      this.syncSelectedValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /* ======================= *
   * Selecao
   * ======================= */
  onSelect(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  /* ======================= *
   * Carga inicial
   * ======================= */
  loadInitial(): void {
    this.loading = true;
    this.unidades = [];

    this.unidadeMedidaService.get('/unidaDeMedidaDeItemEmpresa', { size: 999 }).subscribe({
      next: (res: any) => {
        this.unidades = (res.content || []).map((item: any) => {
          const unidade = item.unidadeMedidaDeItemEmpresa || item;
          return {
            id: item.id,
            unidadeMedidaDeItemEmpresa: {
              id: unidade.id,
              nome: unidade.nome,
              tipoUnidade: unidade.tipoUnidade,
              casasDecimais: unidade.casasDecimais ?? 0
            }
          };
        });
        this.syncSelectedValue();
      },
      error: () => {
        this.unidades = [];
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  /* ======================= *
   * Sincronizacao edicao
   * ======================= */
  private syncSelectedValue(): void {
    if (!this.value) return;

    // Se o value eh um numero (ID), buscar o objeto na lista e selecionar
    if (typeof this.value === 'number') {
      const found = this.unidades.find(u => u.unidadeMedidaDeItemEmpresa?.id === this.value);
      if (found) {
        if (this.returnType === 'object') {
          this.value = found;
          this.onChange(found);
        }
        // Para returnType='id', o valor ja esta correto (eh o id)
      }
    }
  }

  /* ======================= *
   * Dialog de nova unidade
   * ======================= */

  /**
   * Handler do click no footer "+ Agregar Unidade de Medida".
   * Usa (mousedown) com stopPropagation para impedir
   * que o dropdown feche antes de processar o clique.
   */
  onAddNew(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogVisible = true;
  }

  /**
   * Handler chamado quando o dialog emite uma unidade criada.
   * Insere a unidade no inicio da lista e a seleciona automaticamente.
   */
  onUnidadeCriada(unidade: any): void {
    // O dialog emite: { id, nome, unidadeMedidaDeItemEmpresa: { id, nome, ... } }
    const unidadeInterna = unidade?.unidadeMedidaDeItemEmpresa || unidade;
    const novaUnidade = {
      id: unidade.id,
      unidadeMedidaDeItemEmpresa: {
        id: unidadeInterna.id,
        nome: unidadeInterna.nome,
        tipoUnidade: unidadeInterna.tipoUnidade || unidadeInterna.nome,
        casasDecimais: unidadeInterna.casasDecimais ?? 0
      }
    };

    // Inserir a unidade no inicio da lista
    this.unidades = [novaUnidade, ...this.unidades];

    // Selecionar a unidade recem-criada
    if (this.returnType === 'id') {
      this.value = novaUnidade.unidadeMedidaDeItemEmpresa.id;
    } else {
      this.value = novaUnidade;
    }

    // Propagar a mudanca para o formulario pai
    this.onChange(this.value);
  }

  /* ======================= *
   * Util
   * ======================= */
  private loadTextoAgregar(): void {
    this.textoAgregarUnidade = this.translate.instant('select-unidade-medida.botoes.agregar_unidade');
  }
}
